import React, { Fragment, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select'
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'
import Button from "../../../Button";
import makeAnimated from 'react-select/animated';
import styles from './StudentImport.module.sass'
import ClipLoader from 'react-spinners/ClipLoader';
import ImportPreview from './ImportPreview';
import SeriousWarning from './SeriousWarning';
import { findHeaderMatch, FIELD_LABELS, STAFF_FIELD_LABELS } from '../../../../utils/headerMatch';

const HeaderMatcher = ({ formData, school, headers, staffHeaders = [], closeModal }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentImport, setStudentImport] = useState({});
  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [previewAcknowledged, setPreviewAcknowledged] = useState(false);
  const [pairedReview, setPairedReview] = useState(null);
  const [pairedReviewLoading, setPairedReviewLoading] = useState(false);
  const [rowPreview, setRowPreview] = useState(null);
  const [rowPreviewLoading, setRowPreviewLoading] = useState(false);

  const { register, handleSubmit, control } = useForm();
  const animatedComponents = makeAnimated();
  // On a staff file the ID column is the employee number -- same field, shared
  // storage (students.student_id). STAFF_FIELD_LABELS is the one shared
  // definition (headerMatch.js), also used by ImportPreview's column checks.
  const csvHeaders = (formData && formData.get('staff_file') === 'on')
    ? STAFF_FIELD_LABELS
    : FIELD_LABELS;
  // A companion staff file needs far fewer columns than a full roster. The
  // employee ID shares the student_id field (one identity column server-side).
  const staffCsvHeaders = { first_name: 'First Name', last_name: 'Last Name', student_id: 'Employee ID (optional)' }
  const staffRequiredFields = ['first_name', 'last_name']
  const hasStaffFile = staffHeaders.length > 0

  // Staff files have no grade column — every row is forced to 'ST' server-side.
  const isStaffFile = formData && formData.get('staff_file') === 'on'
  const requiredFields = isStaffFile ? ['first_name', 'last_name'] : ['first_name', 'last_name', 'grade']

  const onSubmit = (data) => {
    setIsSubmitting(true);

    // Staff-prefixed fields (staff_first_name/staff_last_name) are mapped
    // against staffHeaders, not headers — split them out into their own
    // mapping so the server can't confuse the two files' columns.
    const primaryData = {};
    const staffData = {};
    Object.keys(data).forEach((key) => {
      if (key.startsWith('staff_')) {
        staffData[key.replace('staff_', '')] = data[key];
      } else {
        primaryData[key] = data[key];
      }
    });

    formData.append('headers', JSON.stringify(primaryData));
    if (hasStaffFile) formData.append('staff_headers', JSON.stringify(staffData));
    formData.append('school_id', school.value);

    fetch('/admin/student_imports',
      { method: 'POST', body: formData })
      .then(res => res.json())
      .then(json => {
        setStudentImport(json);
        // Nothing has been written yet - the roster is only imported once the
        // operator OKs the single consolidated review screen below. All three
        // previews are independent reads, so they run together rather than
        // gating one screen behind another -- three separate "Continue"
        // clicks for three non-gating review steps was the friction this
        // consolidation exists to remove.
        fetchRowPreview(json.id);
        fetchComparison(json.id);
        if (json.paired_import) fetchPairedReview(json.id);
      })
      .catch((error) => {
        toast('There was an error processing the CSV file. Please try again or use a different file.', { type: 'error' })
        setIsSubmitting(false)
      })
  }

  const fetchComparison = (id) => {
    setComparisonLoading(true);

    fetch(`/admin/student_imports/${id}/prior_season_comparison`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) setComparisonError(json.error);
        setComparison(json);
        setComparisonLoading(false);
      })
      .catch(() => {
        setComparisonError('Could not compare this file to last season. Review the file before importing.');
        setComparisonLoading(false);
      })
  }

  // Cross-check + LLM anomaly review for a paired (student + staff) import --
  // nil for an unpaired one. Runs alongside fetchRowPreview/fetchComparison,
  // not gated behind either; ImportPreview renders whatever has resolved
  // once all three have (see the combined `previewLoading` below).
  const fetchPairedReview = (id) => {
    setPairedReviewLoading(true);

    fetch(`/admin/student_imports/${id}/paired_review`)
      .then((res) => res.json())
      .then((json) => {
        setPairedReview(json);
        setPairedReviewLoading(false);
      })
      .catch(() => {
        setPairedReview({ available: false, error: 'Could not build the paired-file review.' });
        setPairedReviewLoading(false);
      })
  }

  // Row-by-row "will this create/update a student, match/create a teacher,
  // who'd be unenrolled" preview. Runs alongside fetchPairedReview/
  // fetchComparison, not gated behind either.
  const fetchRowPreview = (id) => {
    setRowPreviewLoading(true);

    fetch(`/admin/student_imports/${id}/row_level_preview`)
      .then((res) => res.json())
      .then((json) => {
        setRowPreview(json);
        setRowPreviewLoading(false);
      })
      .catch(() => {
        setRowPreview({ available: false, error: 'Could not build the row-level preview.' });
        setRowPreviewLoading(false);
      })
  }

  // The preview is a review step, not a gate: once acknowledged the flow falls
  // through to the existing blocker/warning screens exactly as before.
  const handlePreviewContinue = () => {
    setPreviewAcknowledged(true);

    const hasBlockers = studentImport.import_blockers && studentImport.import_blockers.length > 0;

    if (!studentImport.overwrite_warning && !studentImport.teacher_missing_error
      && !studentImport.teacher_reassignment_warning && !hasBlockers) {
      handleContinue(false, studentImport.id)
    }
  }

  const handleContinue = (unenroll, id = undefined, force = false) => {
    setIsSubmitting(true)

    fetch(`/admin/student_imports/${id || studentImport.id}/import_csv?unenroll=${unenroll}&force=${force}`)
      .then(() => {
        window.location.href = `/admin/student_imports/${id || studentImport.id}`
      })
      .catch((error) => {
        toast('There was an error processing the CSV file. Please try again or use a different file.', { type: 'error' })
        setIsSubmitting(false)
      })
  }

  const renderHeaderOptions = () => (
    headers.map((header) => ({ label: header, value: header }))
  )

  // The guess lives in utils/headerMatch, derived from the 43,947 header
  // choices operators have already made (student_imports.header_mapping) and
  // tested against them. The rule that was here -- raw case-sensitive
  // `includes` -- auto-matched 66% of those; this matches 97.9%, and the
  // difference is mostly byte-order marks, casing and a curated alias table.
  const findDefaultValue = (key, value) => findHeaderMatch(headers, key, value);

  const renderInputs = () => {
    const keys = Object.keys(csvHeaders);

    if (isSubmitting) {
      return (
        <div className={styles.loadingContainer}>
          <p>Importing student CSV for {school.label}. Please wait until you're redirected.</p>
          <ClipLoader color={'blue'} loading={true} size={150} />
        </div>
      )
    }

    return (
      keys.map((key, i) => {
        return (
          <label key={key}>
            {csvHeaders[key]}
            <Controller
              control={control}
              defaultValue={findDefaultValue(key, csvHeaders[key])}
              name={key}
              render={({ field: { onChange, value } }) => (
                <Select
                  {...register(key, { required: requiredFields.includes(key) })}
                  menuPlacement={i > 3 ? 'top' : 'bottom'}
                  onChange={({ value: newValue }) => onChange({ label: newValue, value: newValue })}
                  className="basic-single"
                  components={animatedComponents}
                  defaultValue={findDefaultValue(key, csvHeaders[key])}
                  options={renderHeaderOptions()}
                  name={key}
                />
              )}
            />
          </label>
        )
      })
    )
  }

  const renderStaffInputs = () => {
    if (!hasStaffFile || isSubmitting) return null;

    const staffOptions = staffHeaders.map((header) => ({ label: header, value: header }))
    // Same matcher as the roster columns, so 'Emp ID' / 'Employee Number' land
    // on the Employee ID select via the student_id alias table.
    const findStaffDefault = (key, label) => findHeaderMatch(staffHeaders, key, label)

    return (
      <>
        <p className={styles.importInstructions}>Staff/teacher file columns</p>
        {Object.keys(staffCsvHeaders).map((key, i) => (
          <label key={`staff_${key}`}>
            {staffCsvHeaders[key]}
            <Controller
              control={control}
              defaultValue={findStaffDefault(key, staffCsvHeaders[key])}
              name={`staff_${key}`}
              render={({ field: { onChange } }) => (
                <Select
                  {...register(`staff_${key}`, { required: staffRequiredFields.includes(key) })}
                  menuPlacement={i > 0 ? 'top' : 'bottom'}
                  onChange={({ value: newValue }) => onChange({ label: newValue, value: newValue })}
                  className="basic-single"
                  components={animatedComponents}
                  defaultValue={findStaffDefault(key, staffCsvHeaders[key])}
                  options={staffOptions}
                  name={`staff_${key}`}
                />
              )}
            />
          </label>
        ))}
      </>
    )
  }

  // One consolidated review screen: row-level create/update/teacher
  // preview, the paired-file cross-check + LLM anomaly pass (when this is a
  // paired import), and the year-over-year comparison + column check, all
  // rendered together through ImportPreview's stat-card/severity-finding
  // language rather than three separately-styled, separately-gated
  // screens. None of the three is a gate -- they're all independent reads
  // fired together in onSubmit -- so one shared spinner covering all three
  // is honest, not a shortcut: the operator was always going to wait for
  // the slowest of the three regardless of how many screens showed it.
  if (studentImport.id && !previewAcknowledged) {
    const previewLoading = rowPreviewLoading || comparisonLoading
      || (studentImport.paired_import && pairedReviewLoading)

    return (
      <ImportPreview
        comparison={comparison}
        loading={previewLoading}
        error={comparisonError}
        rowPreview={rowPreview}
        pairedReview={studentImport.paired_import ? pairedReview : null}
        staffFile={isStaffFile}
        blockers={studentImport.import_blockers}
        onCancel={closeModal}
        onContinue={handlePreviewContinue}
      />
    )
  }

  if (studentImport.import_blockers && studentImport.import_blockers.length > 0) {
    return (
      <>
        <SeriousWarning
          title="This import has been stopped"
          items={studentImport.import_blockers.map((blocker) => ({ key: blocker, message: blocker }))}
          note={`Only import anyway if you are certain this file belongs to ${school.label}.`}
        />

        <div className={styles.buttonContainer}>
          <button type="button" className={`btn btn-default ${styles.btn}`} onClick={closeModal}>
            Cancel — do not import
          </button>
          <button
            type="button"
            className={`btn btn-danger ${styles.btn}`}
            onClick={() => handleContinue(false, undefined, true)}
          >
            I have checked — import anyway
          </button>
        </div>
      </>
    )
  }

  if (studentImport.teacher_missing_error) {
    return (
      <>
        <div className={styles.buttonContainer}>
          <a type="button" className={`btn btn-primary ${styles.btn}`} onClick={() => handleContinue(false)}>
            Continue anyway
          </a>
        </div>

        <p className={styles.warning}>
          {studentImport.teacher_missing_error}
        </p>
      </>
    )
  }

  if (studentImport.teacher_reassignment_warning) {
    return (
      <>
        <p className={styles.warning}>
          {studentImport.teacher_reassignment_warning}
        </p>

        <div className={styles.buttonContainer}>
          <a type="button" className={`btn btn-primary ${styles.btn}`} onClick={() => handleContinue(false)}>
            Continue and use this file&apos;s teachers
          </a>
        </div>
      </>
    )
  }

  if (studentImport.overwrite_warning) {
    // Extends the count-only warning above with the actual list, when the
    // row-level preview computed one (it's left unavailable for a truncated
    // file -- see RowLevelPreview -- so this can legitimately be absent).
    const candidates = rowPreview && !rowPreview.truncated && rowPreview.unenrollment_candidates

    return (
      <>
        <p className={styles.warning}>
          {studentImport.overwrite_warning}
        </p>

        {candidates && candidates.length > 0 && (
          <ul className={styles.warning}>
            {candidates.map((student) => (
              <li key={student.student_id}>
                {student.name}
                {' ('}
                {student.student_id}
                )
              </li>
            ))}
          </ul>
        )}

        <div className={styles.buttonContainer}>
          <a type="button" className={`btn btn-primary ${styles.btn}`} onClick={() => handleContinue(false)}>
            No, keep existing students and process this CSV
          </a>

          <a type="button" className={`btn btn-danger ${styles.btn}`} onClick={() => handleContinue(true)}>
            Yes, unenroll all students that are not present in this CSV
          </a>
        </div>
      </>
    )
  }


  return (
    <Fragment>
      {!isSubmitting && (
        <p className={styles.importInstructions}>
          Match headers to import {isStaffFile ? 'STAFF' : 'students'} into {school.label}
          {isStaffFile && ' — every row will be imported with grade ST'}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {renderInputs()}
        {renderStaffInputs()}

        {!isSubmitting && (
          <Button
            fullWidth
            classes={styles.button}
            variant='primary'
            disabled={isSubmitting}
          >
            Continue
          </Button>
        )}
      </form>
    </Fragment>
  )
}

export default HeaderMatcher;
